<template>
  <div>
    <vs-button @click="goBack" color="dark" type="filled" icon-pack="feather" icon="icon-arrow-left"> Back </vs-button>

    <div class="room-management">
      <div class="w-full px-4 my-5">
        <!-- Header -->
        <div class="flex justify-between items-center mb-6">
          <div>
            <h3 class="text-2xl font-semibold capitalize">Room Management - {{ saraiName }}</h3>
            <p class="text-gray-600 mt-1 room-card-title-text">Manage individual rooms by variant</p>
          </div>

        </div>
        <vs-divider></vs-divider>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-12">
          <vs-progress indeterminate color="primary"></vs-progress>
          <p class="mt-4 text-gray-600">Loading rooms...</p>
        </div>

        <!-- Room Variants -->

        <div v-else-if="roomVariants && roomVariants.length > 0" class="room-variants-container">

          <div v-for="variant in roomVariants" :key="variant.room_type" class="variant-group mb-6">
            <div class="variant-card border rounded-lg p-6">
              <!-- Variant Header -->
              <div class="flex justify-between items-center mb-4 pb-3 border-b">
                <div>
                  <h4 style="font-size: 20px !important;" class=" font-semibold mb-3">{{
                    formatRoomType(variant.room_type) }}</h4>
                  <p class="room-card-details text-gray-600">
                    <span class="font-semibold">Total:</span> {{ calculateTotalRoomsInVariant(variant.rooms) }} rooms |
                    <span class="font-semibold"> Active:</span> {{ getActiveCount(variant.rooms) }} |
                    <span class="font-semibold">Inactive: </span> {{ getInactiveCount(variant.rooms) }}
                  </p>
                </div>
              </div>

              <!-- Rooms List -->
              <div class="rooms-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                <div v-for="room in variant.rooms" :key="room._id || room.temp_id"
                  class="room-card border rounded-lg px-4 py-8"
                  :class="{ 'room-card--inactive': !room.is_active, 'room-card--active': room.is_active }">
                  <div class="flex justify-between items-center ">
                    <div class="flex-1">
                      <h5 class="font-semibold room-card-title-text capitalize">{{ room.name }}</h5>

                    </div>
                    <div class="flex gap-2 room-card-title-text">
                      <span class="font-semibold ">Rs. </span> {{ formatCurrency(variant.price_per_night) }}/Night

                    </div>

                  </div>
                  <vs-divider></vs-divider>

                  <div class="mb-5">
                    <p class="room-card-details text-gray-600 mt-1" v-if="room.bed_type"> <span
                        class="font-semibold">Bed Type:</span> {{ room.bed_type }}</p>
                    <p class="room-card-details  mt-3 text-gray-600" v-if="room.room_size"> <span
                        class="font-semibold">Room Size:</span> {{ room.room_size }}</p>
                    <p class="room-card-details mt-3 text-gray-600">
                      <span class="font-semibold">Bookings: </span>
                      <span class="text-orange-600">{{ room.booking_count || 0 }}</span>
                    </p>
                  </div>

                  <div class="gap-5 flex flex-col ">
                    <div class="w-fit">

                      <vs-chip :color="room.is_active ? 'success' : 'warning'" size="small">
                        {{ room.is_active ? 'Active' : 'Inactive' }}
                      </vs-chip>
                    </div>
                    <div class="flex justify-items-center gap-2">
                      <vs-button @click="toggleRoomStatus(room)" type="filled"
                        :disabled="room.booking_count > 0 && !room.is_active"
                        class="flex items-center justify-center px-4 rounded-lg border font-semibold room-status-btn h-10">
                        {{ room.is_active ? 'Inactivate' : 'Activate' }}
                      </vs-button>
                      <!-- <vs-button 
                      @click="deleteRoom(room)" 
                      type="filled"
                      :disabled="room.booking_count > 0"
                      class="flex items-center justify-center px-4 rounded-lg border-gray-300 border text-black font-semibold bg-grey-light h-10"

                    >
                      Delete
                    </vs-button> -->
                    </div>
                  </div>


                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-12">
          <p class="text-gray-500">No rooms found</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axios'

export default {
  name: 'RoomManagement',
  data() {
    return {
      saraiId: null,
      saraiName: '',
      loading: false,
      rooms: [],
      roomVariants: []
    }
  },
  mounted() {
    this.saraiId = this.$route.params.saraiId
    if (this.saraiId) {
      this.loadRooms()
    }

  },
  methods: {
    async loadRooms() {
      if (!this.saraiId) return

      this.loading = true
      try {
        const response = await axios.get(`/admin/sarai/${this.saraiId}`)

        if (response.data.success) {
          const data = response.data.data
          this.saraiName = data.name
          this.rooms = data.rooms || []
          this.groupRoomsByVariant()
        }
      } catch (error) {
        console.error('Error loading rooms:', error)
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to load rooms',
          color: 'danger'
        })
      } finally {
        this.loading = false
      }
    },
    groupRoomsByVariant() {
      // Group rooms by room_type and price_per_night
      const variantMap = {}

      this.rooms.forEach(room => {
        const key = `${room.room_type}_${room.price_per_night}`
        if (!variantMap[key]) {
          variantMap[key] = {
            room_type: room.room_type,
            price_per_night: room.price_per_night,
            rooms: []
          }
        }
        variantMap[key].rooms.push(room)
      })

      // Load booking counts for each room
      this.loadBookingCounts(Object.values(variantMap))
    },
    async loadBookingCounts(variants) {
      // Get booking counts for each room
      try {
        const response = await axios.get(`/admin/sarai/${this.saraiId}/room-bookings`)
        const bookingCounts = response.data.data || {}

        variants.forEach(variant => {
          variant.rooms.forEach(room => {
            room.booking_count = bookingCounts[room._id] || 0
          })
        })

        this.roomVariants = variants
      } catch (error) {
        // If endpoint doesn't exist, set booking count to 0
        variants.forEach(variant => {
          variant.rooms.forEach(room => {
            room.booking_count = 0
          })
        })
        this.roomVariants = variants
      }
    },
    async toggleRoomStatus(room) {
      if (room.booking_count > 0 && !room.is_active) {
        this.$vs.notify({
          title: 'Cannot Activate',
          text: 'Cannot activate room with active bookings',
          color: 'warning'
        })
        return
      }

      const newStatus = !room.is_active
      const action = newStatus ? 'activate' : 'inactivate'

      this.$vs.dialog({
        type: 'confirm',
        color: newStatus ? 'success' : 'warning',
        title: `Confirm ${action.charAt(0).toUpperCase() + action.slice(1)}`,
        text: `Are you sure you want to ${action} this room?`,
        accept: async () => {
          try {
            await axios.put(`/admin/sarai/${this.saraiId}/room/${room._id}/status`, {
              is_active: newStatus
            })

            this.$vs.notify({
              title: 'Success',
              text: `Room ${action}d successfully`,
              color: 'success'
            })

            room.is_active = newStatus
          } catch (error) {
            this.$vs.notify({
              title: 'Error',
              text: error.response && error.response.data && error.response.data.message
                ? error.response.data.message
                : `Failed to ${action} room`,
              color: 'danger'
            })
          }
        },
        acceptText: action.charAt(0).toUpperCase() + action.slice(1)
      })
    },
    async deleteRoom(room) {
      if (room.booking_count > 0) {
        this.$vs.notify({
          title: 'Cannot Delete',
          text: 'Cannot delete room with bookings. Please deactivate it instead.',
          color: 'warning'
        })
        return
      }

      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Confirm Delete',
        text: 'Are you sure you want to delete this room? This action cannot be undone.',
        accept: async () => {
          try {
            await axios.delete(`/admin/sarai/${this.saraiId}/room/${room._id}`)

            this.$vs.notify({
              title: 'Success',
              text: 'Room deleted successfully',
              color: 'success'
            })

            // Reload rooms
            this.loadRooms()
          } catch (error) {
            this.$vs.notify({
              title: 'Error',
              text: error.response && error.response.data && error.response.data.message
                ? error.response.data.message
                : 'Failed to delete room',
              color: 'danger'
            })
          }
        },
        acceptText: 'Delete'

      })
    },
    getActiveCount(rooms) {
      return rooms.reduce((total, r) => total + (r.is_active ? (Number(r.number_of_rooms) || 1) : 0), 0)
    },
    getInactiveCount(rooms) {
      return rooms.reduce((total, r) => total + (!r.is_active ? (Number(r.number_of_rooms) || 1) : 0), 0)
    },
    calculateTotalRoomsInVariant(rooms) {
      if (!rooms || !Array.isArray(rooms)) return 0
      return rooms.reduce((total, room) => total + (Number(room.number_of_rooms) || 1), 0)
    },
    formatCurrency(amount) {
      if (!amount) return '0'
      return new Intl.NumberFormat('en-IN').format(amount)
    },
    formatRoomType(type) {
      const types = {
        '1_bed_ac': '1 Bed AC',
        '1_bed_non_ac': '1 Bed Non-AC',
        '2_bed_ac': '2 Bed AC',
        '2_bed_non_ac': '2 Bed Non-AC',
        dormitory_hall: 'Dormitory Hall'
      }
      return types[type] || type
    },
    goBack() {
      this.$router.go(-1)
    }
  }
}
</script>

<style scoped>
.room-management {
  background: var(--page-bg);
  color: var(--text-primary);
  padding: 12px;
  margin-top: 20px
}

.variant-card {
  background: var(--card-bg);
  border-color: var(--divider);
}

.room-card--active {
  background: var(--page-bg);
}

.room-card--inactive {
  background: var(--card-bg);
  opacity: 0.85;
}

.room-status-btn {
  color: var(--text-primary) !important;
  background-color: var(--card-bg) !important;
  border-color: var(--divider) !important;
}

.room-card-title-text {
  font-size: 18px !important;
}

.room-card {
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

}

.room-card-details {
  font-size: 17px !important;
}

.room-card:hover {
  transform: translateY(-2px);
}

.theme-dark .text-gray-500,
.theme-dark .text-gray-600 {
  color: var(--text-secondary) !important;
}
</style>
